/**
 * Analytics Pipeline API
 * Provides functions for interacting with the Analytics Pipeline backend
 */
import { ENDPOINTS } from './endpoints';
import { getBaseApiUrl } from './core';
import logger from '../utils/logger';

const getAuthHeaders = async (auth) => {
  const idToken = await auth.currentUser.getIdToken(false);
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
  };
};

// ==================== Library Modules ====================

/**
 * Get all available library modules
 */
export const getLibraryModules = async (auth, callback, category = null) => {
  try {
    const headers = await getAuthHeaders(auth);
    let url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.LIBRARY_MODULES;
    if (category) {
      url += `?category=${encodeURIComponent(category)}`;
    }

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching library modules:', error);
    callback({ error: error.message });
  }
};

// ==================== Pipeline Management ====================

/**
 * Get all pipelines for user (optionally filtered by instrument)
 */
export const getPipelines = async (auth, callback, instrument = null) => {
  try {
    const headers = await getAuthHeaders(auth);
    let url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINES;
    if (instrument) {
      url += `?instrument=${encodeURIComponent(instrument)}`;
    }

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching analytics pipelines:', error);
    callback({ error: error.message });
  }
};

/**
 * Get active pipeline for an instrument
 */
export const getActivePipeline = async (auth, callback, instrument) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINES_ACTIVE + `?instrument=${encodeURIComponent(instrument)}`;

    const response = await fetch(url, { method: 'GET', headers });

    if (response.status === 404) {
      callback(null);
      return;
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching active pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Get a specific pipeline by ID
 */
export const getPipeline = async (auth, callback, pipelineId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE(pipelineId);

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Create a new pipeline
 */
export const createPipeline = async (auth, callback, { instrument, name, description, defaultTimeframe }) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINES;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ instrument, name, description, defaultTimeframe }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error creating pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Update a pipeline
 */
export const updatePipeline = async (auth, callback, pipelineId, { name, description, defaultTimeframe }) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE(pipelineId);

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ name, description, defaultTimeframe }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error updating pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Activate a pipeline
 */
export const activatePipeline = async (auth, callback, pipelineId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_ACTIVATE(pipelineId);

    const response = await fetch(url, { method: 'PATCH', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error activating pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Delete a pipeline
 */
export const deletePipeline = async (auth, callback, pipelineId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE(pipelineId);

    const response = await fetch(url, { method: 'DELETE', headers });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    callback({ success: true });
  } catch (error) {
    logger.error('Error deleting pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Duplicate a pipeline
 */
export const duplicatePipeline = async (auth, callback, pipelineId, newName) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_DUPLICATE(pipelineId);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name: newName }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error duplicating pipeline:', error);
    callback({ error: error.message });
  }
};

// ==================== Pipeline Modules ====================

/**
 * Get modules for a pipeline
 */
export const getPipelineModules = async (auth, callback, pipelineId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_MODULES(pipelineId);

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching pipeline modules:', error);
    callback({ error: error.message });
  }
};

/**
 * Add a module to a pipeline
 */
export const addModuleToPipeline = async (auth, callback, pipelineId, { libraryModuleId, customConfigJson, weight }) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_MODULES(pipelineId);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ libraryModuleId, customConfigJson, weight }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error adding module to pipeline:', error);
    callback({ error: error.message });
  }
};

/**
 * Update a pipeline module
 */
export const updatePipelineModule = async (auth, callback, pipelineId, moduleId, { customConfigJson, weight, enabled }) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_MODULE(pipelineId, moduleId);

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ customConfigJson, weight, enabled }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error updating pipeline module:', error);
    callback({ error: error.message });
  }
};

/**
 * Remove a module from a pipeline
 */
export const removePipelineModule = async (auth, callback, pipelineId, moduleId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_MODULE(pipelineId, moduleId);

    const response = await fetch(url, { method: 'DELETE', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    callback({ success: true });
  } catch (error) {
    logger.error('Error removing pipeline module:', error);
    callback({ error: error.message });
  }
};

// ==================== Webhook ====================

/**
 * Update webhook enabled status
 */
export const updateWebhookEnabled = async (auth, callback, pipelineId, enabled) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_WEBHOOK(pipelineId);

    const response = await fetch(url, {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ enabled }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error updating webhook:', error);
    callback({ error: error.message });
  }
};

/**
 * Regenerate webhook secret
 */
export const regenerateWebhookSecret = async (auth, callback, pipelineId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.PIPELINE_WEBHOOK_REGENERATE(pipelineId);

    const response = await fetch(url, { method: 'POST', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error regenerating webhook secret:', error);
    callback({ error: error.message });
  }
};

// ==================== Analysis Execution ====================

/**
 * Run analysis (synchronous)
 */
export const runAnalysis = async (auth, callback, { instrument, timeframe, pipelineId }) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.ANALYZE;

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ instrument, timeframe, pipelineId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error running analysis:', error);
    callback({ error: error.message });
  }
};

// ==================== History ====================

/**
 * Get analysis history
 */
export const getHistory = async (auth, callback, { instrument, limit = 50, offset = 0 } = {}) => {
  try {
    const headers = await getAuthHeaders(auth);
    let url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.HISTORY + `?limit=${limit}&offset=${offset}`;
    if (instrument) {
      url += `&instrument=${encodeURIComponent(instrument)}`;
    }

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching history:', error);
    callback({ error: error.message });
  }
};

/**
 * Get execution details
 */
export const getExecution = async (auth, callback, executionId) => {
  try {
    const headers = await getAuthHeaders(auth);
    const url = getBaseApiUrl() + ENDPOINTS.ANALYTICS.EXECUTION(executionId);

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    callback(data);
  } catch (error) {
    logger.error('Error fetching execution:', error);
    callback({ error: error.message });
  }
};
